/**
 * Drift-correction policy for venue-grade multi-room sync.
 *
 * Given the measured drift between a device's actual playback position and
 * the timeline-expected position (drift = actual − expected, seconds), decide
 * whether to do nothing, gently rate-nudge back into alignment, or hard-seek.
 * Pure decision logic — each surface applies the action to its own player
 * (playbackRate on media elements / expo-audio, seek fallback elsewhere).
 *
 * Shape: below the soft threshold nothing happens (normal jitter); between
 * soft and hard a rate nudge (inaudible ±3%) walks the position back;
 * at/above hard the listener would already notice, so seek once. Hysteresis:
 * once nudging, keep nudging until drift falls under the release threshold —
 * without it a device hovering at the soft threshold would flap between
 * nudging and not, which is audible as pitch wobble.
 */

export interface DriftCorrectionOptions {
  /** |drift| below this (and not already nudging) → no action. */
  softThresholdSec: number;
  /** |drift| at/above this → hard seek. */
  hardThresholdSec: number;
  /** While nudging, stop only when |drift| falls below this. */
  releaseThresholdSec: number;
  /** Rate delta for nudges: ahead → 1-delta (slow down), behind → 1+delta. */
  nudgeRateDelta: number;
}

export const DEFAULT_DRIFT_OPTIONS: DriftCorrectionOptions = {
  softThresholdSec: 0.35,
  hardThresholdSec: 2,
  releaseThresholdSec: 0.1,
  nudgeRateDelta: 0.03,
};

export type DriftAction =
  | { kind: "none"; nudging: false }
  | { kind: "nudge"; rate: number; nudging: true }
  | { kind: "seek"; nudging: false };

/**
 * Decide the correction for a drift measurement.
 *
 * @param driftSec actual − expected position (positive = device is ahead)
 * @param wasNudging whether the previous decision was a nudge (hysteresis)
 */
export function driftCorrectionAction(
  driftSec: number,
  wasNudging: boolean,
  opts: DriftCorrectionOptions = DEFAULT_DRIFT_OPTIONS,
): DriftAction {
  if (!Number.isFinite(driftSec)) return { kind: "none", nudging: false };

  const magnitude = Math.abs(driftSec);
  if (magnitude >= opts.hardThresholdSec) {
    return { kind: "seek", nudging: false };
  }

  const engage = wasNudging
    ? magnitude >= opts.releaseThresholdSec
    : magnitude >= opts.softThresholdSec;
  if (!engage) return { kind: "none", nudging: false };

  const rate = driftSec > 0 ? 1 - opts.nudgeRateDelta : 1 + opts.nudgeRateDelta;
  return { kind: "nudge", rate, nudging: true };
}
